import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from '../Form';
import { useSnackbar } from 'notistack';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import ContactForm from '../ContactForm';
import ContactSearch from '../ContactSearch';
import FormDrawer from '../FormDrawer';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import getContactDisplay from '@afosto/utils/getContactDisplay';
import translations from './translations';

const ContactFormSection = ({ name, onChange, value }) => {
  const intl = useIntl();
  const [showCreateContact, setShowCreateContact] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const formContext = useFormContext();
  const { setValue, watch } = formContext || {};
  const inputValue = value ?? (watch && watch(name));

  const handleChangeContact = (ev, newValue) => {
    if (formContext && !onChange) {
      setValue(name, newValue);
      return;
    }

    if (onChange && typeof onChange === 'function') {
      onChange(ev, newValue);
    }
  };

  /**
   * Handle creating a new contact.
   * @returns {Promise<void>}
   */
  const handleCreateContact = async formData => {
    try {
      const { givenName, additionalName, familyName, ...otherContactData } = formData;
      const response = await contactsApi.upsertContact({
        ...otherContactData,
        given_name: givenName,
        additional_name: additionalName,
        family_name: familyName,
        is_guest: false,
      });
      const { data = {} } = response;

      enqueueSnackbar(intl.formatMessage(translations.feedback.contactCreated), {
        variant: 'success',
      });

      handleChangeContact(
        {},
        {
          ...data,
          label: getContactDisplay(data),
          value: data.id,
        },
      );
      setShowCreateContact(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.contactNotCreated), {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={intl.formatMessage(translations.page.contactFormSection.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button color="primary" variant="outlined" onClick={() => setShowCreateContact(true)}>
              {intl.formatMessage(translations.page.contactFormSection.button.create.label)}
            </Button>
          }
        />
        <CardContent>
          <ContactSearch
            name={name}
            label={intl.formatMessage(translations.page.contactFormSection.title)}
            onChange={handleChangeContact}
            value={inputValue}
          />
        </CardContent>
      </Card>
      <FormDrawer
        isOpen={showCreateContact}
        onClose={() => setShowCreateContact(false)}
        drawerWidth="400px"
        title={intl.formatMessage(translations.page.createContact.title)}
      >
        <ContactForm onCancel={() => setShowCreateContact(false)} onSubmit={handleCreateContact} />
      </FormDrawer>
    </>
  );
};

ContactFormSection.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.object,
};

ContactFormSection.defaultProps = {
  name: 'contact',
  onChange: undefined,
  value: undefined,
};

export default ContactFormSection;
